import { Divider, Flex } from "@theorgchartdev/storybook";
import React, { useEffect, useRef, useState } from "react";
import { useRecordInfoContext } from "@/providers/RecordInfoProvider";
import { getRecordDataByFieldExclusion } from "@/Utils/DataLib";
import { GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { RecordDetailsFields } from "@/appComponents/Blocks/Cards/RecordInfoCard/RecordDetailsFields";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { RecordEditLabel } from "@/appComponents/Blocks/Composites/RecordEditLabel/RecordEditLabel";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { createFieldOverride } from "@/appComponents/Blocks/Cards/RecordInfoCard/utils";
import {
  CardWrapper,
  DefaultCardDimensions
} from "@/appComponents/Blocks/Cards/CardWrapper/CardWrapper";
import useResizable from "@/hooks/useResizable";
import { appstr } from "@coreapp/utils/appstr";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumObjClassNames } from "@coreapp/constants";
import { getContainerHeight, getContainerWidth } from "@/Utils/getContainerSize";

export const RecordInfoCard = () => {
  const { showRecordInfo, closeRecordInfo, selectedBox } = useRecordInfoContext();
  const [editing, setEditing] = useState(false);
  const deltaRef = useRef({});
  const { canEditDocuments } = usePermissionCheck();
  const doc = GetDoc();

  useChartDocumentContext();

  const { initialHeight, initialWidth, minHeight, minWidth } = DefaultCardDimensions();

  const { size, handleMouseDown } = useResizable(initialWidth, initialHeight);
  const initOffset = {
    x: (getContainerWidth() - initialWidth) / 2,
    y: (getContainerHeight() - initialHeight) / 2
  };

  useEffect(() => {
    // clean up on component unmount
    return () => {
      setEditing(false);
      deltaRef.current = {};
    };
  }, [showRecordInfo]);

  const targetBox = selectedBox;
  if (!doc || !showRecordInfo || !targetBox) return;

  const canAccessEditing =
    canEditDocuments && !doc.IsServerChart() && Globals._uiConfig?.InEditMode();

  const baseClass = "oc-record-info-card";

  const dbObj = targetBox.GetDataObj();
  if (!dbObj) return;

  const recordData = dbObj.JSON() ?? {};
  let recordDataCopy = recordData ? JSON.parse(JSON.stringify(recordData)) : {};
  let otherFieldData = getRecordDataByFieldExclusion(
    recordDataCopy,
    [],
    true,
    false,
    dbObj.InferObjectClassName()
  );

  const handleSave = () => {
    if (!canAccessEditing) return;
    createFieldOverride(dbObj, deltaRef.current);
    deltaRef.current = {};
    setEditing(false);
    const doc = GetDoc();
    if (doc) {
      const canvas = doc.GetCanvasRef();
      canvas.AutoFormat();
      doc.ApplyChartGroupingToDoc();
    }
    ReRenderCanvas();
  };

  const onUpdateField = (fVPair, newValue) => {
    if (!canAccessEditing) return;
    const fieldDef = fVPair.fieldDef;
    if (fieldDef.FieldReadOnly()) return;
    const fieldName = fVPair.fieldName;
    let curValue = dbObj.GetFieldValue(fieldDef);
    // dont write to delta if value unchanged
    if (curValue === newValue || (curValue === null && newValue === "")) return;
    deltaRef.current[fieldName] = newValue;
  };

  const handleReset = (unsavedCheck = true) => {
    if (unsavedCheck && !doUnsavedChangesCheck()) return false;
    deltaRef.current = {};
    setEditing(false);
    return true;
  };

  const onEdit = (mode, justOneField) => {
    if (justOneField) {
      setEditing(true);
      return;
    }

    if (editing) {
      handleSave();
    }
    setEditing(!editing);
  };

  const doUnsavedChangesCheck = closeAfter => {
    const unsavedChanges = Object.keys(deltaRef.current).length > 0;
    if (!unsavedChanges) return true;

    const wsMgr = GetWSMgr();
    wsMgr.CreateReactAlert(
      appstr("Save changes to") + dbObj.FormatEmployeeLabel() + "?",
      appstr("All changes will be lost"),
      () => {
        handleSave();
        if (closeAfter) {
          onClose();
        }
      },
      () => {
        handleReset(false);
        if (closeAfter) {
          onClose();
        }
      },
      appstr("Save Changes"),
      appstr("Discard Changes")
    );
    return false;
  };

  const onClose = () => {
    if (!doUnsavedChangesCheck(true)) return;
    closeRecordInfo();
  };

  return (
    <CardWrapper
      show={showRecordInfo}
      prefixId={"record-details"}
      contentClass={baseClass}
      onClose={onClose}
      focusTrap
      otherHeaderProps={{ title: appstr("Record details") }}
      size={size}
      onMouseDownResize={handleMouseDown}
      initOffset={initOffset}
      minWidth={minWidth}
      minHeight={minHeight}
    >
      <RecordEditLabel
        dbObj={dbObj}
        data={otherFieldData}
        photoSrc={null}
        onSave={handleSave}
        onReset={handleReset}
        onUpdate={onUpdateField}
        deltaRef={deltaRef?.current}
        onEdit={() => onEdit()}
      />
      <Flex direction={"column"} width={"100%"} overflow={"hidden"} height={"100%"} gap={"12px"}>
        <Divider />
        <RecordDetailsFields
          data={otherFieldData}
          className={baseClass}
          dbObj={dbObj}
          deltaRef={deltaRef?.current}
          onSave={handleSave}
          onReset={handleReset}
          onUpdate={onUpdateField}
          editing={canAccessEditing && editing}
          onEdit={onEdit}
        />
      </Flex>
    </CardWrapper>
  );
};
